import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {
  reviewSummaryStateLabels,
  type ReviewSummary,
  type ReviewSummaryState,
} from '../../page-data/payloads/review-summaries'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useReactToReviewSummaryMutation} from '../../mutations/use-react-to-review-summary-mutation'
import {CheckIcon, ChevronRightIcon, DiffIcon, EyeIcon, PeopleIcon} from '@primer/octicons-react'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef, useCallback} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import CircleOcticon from '@github-ui/circle-octicon'
import {Button} from '@primer/react'
import {useLayoutCanComment} from '../../stores/layout-selectors'

interface ReviewSummaryItemProps {
  reviewSummary: ReviewSummary
  pullRequestAuthorLogin: string
  canReact: boolean
  onQuoteReply: (quotedText: string) => void
}

function ReviewStateIcon({state}: {state: ReviewSummaryState}) {
  switch (state) {
    case 'APPROVED':
      return (
        <CircleOcticon
          icon={() => <CheckIcon size={16} />}
          className="bgColor-success-emphasis fgColor-onEmphasis borderColor-default"
          size={24}
        />
      )
    case 'CHANGES_REQUESTED':
      return (
        <CircleOcticon
          icon={() => <DiffIcon size={16} />}
          className="bgColor-danger-emphasis fgColor-onEmphasis borderColor-default"
          size={24}
        />
      )
    case 'COMMENTED':
      return (
        <CircleOcticon
          icon={() => <EyeIcon size={16} />}
          className="bgColor-muted fgColor-onEmphasis borderColor-default"
          size={24}
        />
      )
    case 'DISMISSED':
    default:
      return null
  }
}

export function ReviewSummaryItem({
  reviewSummary,
  pullRequestAuthorLogin,
  canReact,
  onQuoteReply,
}: ReviewSummaryItemProps): React.ReactElement {
  const {mutate: reactToReviewSummaryMutation} = useReactToReviewSummaryMutation()
  const commentRef = useRef<HTMLDivElement>(null)
  const canComment = useLayoutCanComment()

  const onReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      reactToReviewSummaryMutation({
        commentId: reviewSummary.id,
        reaction,
        viewerHasReacted,
      })
    },
    [reactToReviewSummaryMutation, reviewSummary.id],
  )

  const hasBody = reviewSummary.body.trim().length > 0

  const handleQuoteClick = useCallback(() => {
    if (!hasBody) return
    const current = ssrSafeWindow?.getSelection()
    let currentSelection: SelectionContext | null = null

    if (current && current.anchorNode && current.rangeCount > 0) {
      currentSelection = {
        anchorNode: current.anchorNode,
        range: current.getRangeAt(0),
      }
    }

    const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, reviewSummary.body)
    onQuoteReply(quotedText ?? reviewSummary.body)
  }, [hasBody, onQuoteReply, reviewSummary.body])
  const reviewStateIcon = <ReviewStateIcon state={reviewSummary.state} />
  const reviewStateClasses = `pt-2 tmp-px-3 d-flex flex-items-center flex-justify-between${!hasBody ? ' pb-2' : ''}`

  return (
    <div id={`review-summary-${reviewSummary.id}`} ref={commentRef} className="border rounded-2">
      <div className={reviewStateClasses}>
        <div className="d-flex flex-items-center gap-2 min-width-0 flex-1">
          <ActivityHeader
            avatarUrl={reviewSummary.authorAvatarUrl || ''}
            additionalUserIcon={reviewStateIcon ? <div style={{marginLeft: -8}}>{reviewStateIcon}</div> : undefined}
            comment={{
              id: reviewSummary.id,
              authorAssociation: reviewSummary.authorAssociation,
              createdAt: reviewSummary.submittedAt,
              isHidden: false,
              minimizedReason: null,
              repository: reviewSummary.repository,
              url: reviewSummary.url,
            }}
            commentAuthorLogin={reviewSummary.authorLogin}
            commentSubjectType="pull request"
            commentSubjectAuthorLogin={pullRequestAuthorLogin}
            forceInlineAvatar
            isMinimized={false}
            noContainerStyles
            tooltipDirection="sw"
            additionalHeaderMessage={<>{reviewSummaryStateLabels[reviewSummary.state]}&nbsp;</>}
          />
        </div>
      </div>
      {reviewSummary.onBehalfOf && (
        <div className="tmp-px-3 pt-1 fgColor-muted text-italic">
          <PeopleIcon size={16} />
          &nbsp;for <strong>{reviewSummary.onBehalfOf} </strong>
        </div>
      )}
      {hasBody && (
        <div className="tmp-px-3 py-2">
          <VerifiedHTMLDiv className="markdown-body mt-1 f5" html={reviewSummary.bodyHtml} />
          {reviewSummary.reactionGroups.length > 0 && (
            <div className="mt-2">
              <ReactionViewerBase
                reactionGroups={reviewSummary.reactionGroups}
                canReact={canReact && reviewSummary.viewerCanReact}
                onReact={onReact}
              />
            </div>
          )}
          {canComment && (
            <Button
              className="mt-2 ml-n2"
              onClick={handleQuoteClick}
              size="small"
              trailingVisual={ChevronRightIcon}
              variant="invisible"
            >
              Quote reply
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
